import { Box, Card, CardContent, Divider, Grid, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { FormProvider, RHFRadioGroup, RHFTextField } from "../../common/hook-form";
import { useForm } from "react-hook-form";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { apiConstants, post } from "../../common/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import _ from "lodash";
import { useGetAllCourierPartnersQuery } from "../../redux/commonApiCalls";
import { formatCurrency } from "../../common/util";
import PageTitle from "../../components/PageTitle";
import { FieldLabel } from "../../common/FilterBar";
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

// ─── Section divider ──────────────────────────────────────────────────────────

function SectionLabel({ children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 0.5 }}>
            <Divider sx={{ flex: 1 }} />
            <Box sx={{ fontSize: '9px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px', whiteSpace: 'nowrap' }}>
                {children}
            </Box>
            <Divider sx={{ flex: 1 }} />
        </Box>
    );
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const thSx = {
    fontSize: '10px', fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    bgcolor: '#fafafa', borderBottom: '2px solid #e0e0e0',
    py: 1.25, px: 2,
};
const tdSx = { px: 2, py: 1.25, borderBottom: '1px solid #f0f0f0', fontSize: '13px', color: '#1a1a2e' };

// ─── Component ────────────────────────────────────────────────────────────────

function CalculateRate() {
    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            codAmount:   Yup.number().positive().required("Invoice Value is required!"),
            weight:      Yup.number().positive().required("Weight is required!"),
            height:      Yup.number().positive().required("Height is required!"),
            breadth:     Yup.number().positive().required("Breadth is required!"),
            length:      Yup.number().positive().required("Length is required!"),
            to:          Yup.number().positive().required("Delivery pincode is required!"),
            from:        Yup.number().positive().required("Pickup pincode is required!"),
            paymentType: Yup.string().required("Please select payment mode"),
            orderType:   Yup.string().required("Please select shipment type"),
            merchant:    Yup.string().required("Please select merchant"),
        })),
        defaultValues: {
            codAmount: "", weight: "", height: "", breadth: "", length: "",
            to: "", from: "", paymentType: "", orderType: "", isMerchant: "", merchant: ""
        }
    });
    const { handleSubmit } = methods;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [data, setData] = useState([]);
    const { data: couriers = [] } = useGetAllCourierPartnersQuery();

    const onSubmit = (formData) => {
        setIsSubmitting(true);
        formData.merchantOid = formData.merchant;
        formData.isMerchant = true;
        post(apiConstants.RATE_CALCULATOR, formData)
            .then((res) => { setData(res.data); })
            .catch((err) => console.log(err))
            .finally(() => setIsSubmitting(false));
    };

    const getCourierInfo = (row) => {
        if (!row.provider?.id) return {};
        const found = _.find(couriers, (c) => c.prozoId === row.provider.id);
        return { name: found?.cpAccountCode, mode: found?.dispatchMode };
    };

    // Build enriched + sorted results
    const enriched = _.sortBy(
        (data || []).map((row) => {
            const { name, mode } = getCourierInfo(row);
            return {
                ...row,
                _name: name || row.provider?.id || '—',
                _mode: mode || '',
                _edd: row.providerMaxTatDays ? `${row.providerMaxTatDays} days` : '',
                _price: row.priceAfterTax ?? Infinity,
            };
        }),
        '_price'
    );

    return (
        <>
            <PageTitle>Shipment Rate Calculator (D2C)</PageTitle>

            <Grid container spacing={2.5} alignItems="flex-start">

                {/* ── Form panel (sticky) ────────────────────────────── */}
                <Grid item xs={12} md={5}>
                    <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e8e8e8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', position: { md: 'sticky' }, top: { md: 80 } }}>
                        {/* Header */}
                        <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#e3f2fd', color: '#1565c0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CalculateOutlinedIcon sx={{ fontSize: 18 }} />
                                </Box>
                                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1a1a2e' }}>Rate Calculator</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '11.5px', color: '#9ca3af' }}>Fill in shipment details to compare rates</Typography>
                        </Box>

                        <Divider />

                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                                    {/* Merchant */}
                                    <Box>
                                        <FieldLabel>Merchant *</FieldLabel>
                                        <MerchantFilterCached fullWidth />
                                    </Box>

                                    <SectionLabel>Shipment Options</SectionLabel>

                                    <Box>
                                        <RHFRadioGroup label="Shipment Type *" name="orderType"
                                            options={[{ label: "Forward", value: "FORWARD" }, { label: "Reverse", value: "RVP" }]} />
                                    </Box>
                                    <Box>
                                        <RHFRadioGroup label="Payment Mode *" name="paymentType"
                                            options={[{ label: "Prepaid", value: "PREPAID" }, { label: "COD", value: "COD" }]} />
                                    </Box>

                                    <SectionLabel>Pincodes</SectionLabel>

                                    <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                            <FieldLabel>Pickup *</FieldLabel>
                                            <RHFTextField name='from' label='' placeholder='110001' type='number' fullWidth size="small" InputLabelProps={{ shrink: true }} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FieldLabel>Delivery *</FieldLabel>
                                            <RHFTextField name='to' label='' placeholder='400001' type='number' fullWidth size="small" InputLabelProps={{ shrink: true }} />
                                        </Grid>
                                    </Grid>

                                    <SectionLabel>Package</SectionLabel>

                                    <Grid container spacing={1}>
                                        {[
                                            { name: 'length',  label: 'L', unit: 'cm' },
                                            { name: 'breadth', label: 'W', unit: 'cm' },
                                            { name: 'height',  label: 'H', unit: 'cm' },
                                            { name: 'weight',  label: 'Wt', unit: 'gm' },
                                        ].map(({ name, label, unit }) => (
                                            <Grid item xs={6} sm={3} key={name}>
                                                <FieldLabel>{label} *</FieldLabel>
                                                <RHFTextField name={name} label='' size="small"
                                                    InputProps={{ endAdornment: <InputAdornment position="end"><Box sx={{ fontSize: '10px', color: '#9ca3af' }}>{unit}</Box></InputAdornment> }} />
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <SectionLabel>Invoice</SectionLabel>

                                    <Box>
                                        <FieldLabel>Invoice Amount *</FieldLabel>
                                        <RHFTextField name='codAmount' label='' placeholder='₹ Enter amount' fullWidth size="small" InputLabelProps={{ shrink: true }} />
                                    </Box>

                                    <LoadingButton
                                        loading={isSubmitting}
                                        variant="contained"
                                        size="medium"
                                        type="submit"
                                        fullWidth
                                        disableElevation
                                        startIcon={<LocalShippingOutlinedIcon />}
                                        sx={{ mt: 0.5, fontSize: '13px', fontWeight: 700, textTransform: 'none', py: 1.25, borderRadius: '8px', bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                                    >
                                        Calculate Rates
                                    </LoadingButton>

                                </Box>
                            </FormProvider>
                        </CardContent>
                    </Card>
                </Grid>

                {/* ── Results panel ──────────────────────────────────── */}
                <Grid item xs={12} md={7}>
                    {enriched.length > 0 ? (
                        <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>
                                    Courier-wise Shipping Rates
                                </Typography>
                                <Box sx={{ fontSize: '11px', color: '#9ca3af', bgcolor: '#f3f4f6', px: '8px', py: '2px', borderRadius: '20px' }}>
                                    {enriched.length} option{enriched.length !== 1 ? 's' : ''} · sorted by price
                                </Box>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={thSx}>#</TableCell>
                                            <TableCell sx={thSx}>Courier Partner</TableCell>
                                            <TableCell sx={{ ...thSx, textAlign: 'center' }}>Mode</TableCell>
                                            <TableCell sx={{ ...thSx, textAlign: 'center' }}>EDD</TableCell>
                                            <TableCell sx={{ ...thSx, textAlign: 'right' }}>Delivery Charges</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {enriched.map((row, idx) => (
                                            <TableRow key={idx} sx={{ bgcolor: idx === 0 ? '#eff6ff' : idx % 2 !== 0 ? '#fafbfc' : '#fff', '&:hover': { bgcolor: '#f0f7ff' } }}>
                                                <TableCell sx={{ ...tdSx, color: '#9ca3af', width: 36 }}>{idx + 1}</TableCell>
                                                <TableCell sx={tdSx}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ fontWeight: 600 }}>{row._name}</Box>
                                                        {idx === 0 && (
                                                            <Box component="span" sx={{ fontSize: '9px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', px: '6px', py: '2px', borderRadius: '4px', bgcolor: '#1565c0', color: '#fff' }}>
                                                                Best
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                                                    {row._mode ? (
                                                        <Box component="span" sx={{
                                                            display: 'inline-block', px: '7px', py: '2px', borderRadius: '4px',
                                                            fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                                                            bgcolor: row._mode === 'AIR' ? '#e0f2f1' : '#f3f4f6',
                                                            color: row._mode === 'AIR' ? '#00695c' : '#374151',
                                                        }}>
                                                            {row._mode}
                                                        </Box>
                                                    ) : '—'}
                                                </TableCell>
                                                <TableCell sx={{ ...tdSx, textAlign: 'center', color: '#6b7280' }}>
                                                    {row._edd || '—'}
                                                </TableCell>
                                                <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                                                    <Box sx={{ fontSize: '14px', fontWeight: 800, color: idx === 0 ? '#1565c0' : '#1a1a2e', letterSpacing: '-0.01em' }}>
                                                        {row._price !== Infinity ? formatCurrency(row._price) : '—'}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    ) : (
                        <Box sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            height: 320, borderRadius: '12px', border: '1.5px dashed #e0e0e0', bgcolor: '#fafafa',
                        }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: '12px', bgcolor: '#e3f2fd', color: '#1565c0', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                                <InboxOutlinedIcon sx={{ fontSize: 28 }} />
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1a1a2e', mb: 0.5 }}>No rates yet</Typography>
                            <Typography sx={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', maxWidth: 260 }}>
                                Fill in the form and click <strong>Calculate Rates</strong> to see available courier options.
                            </Typography>
                        </Box>
                    )}
                </Grid>

            </Grid>
        </>
    );
}

export default CalculateRate;
